import { TFunction } from "i18next";

import { faTrashCan, faPenToSquare } from "@fortawesome/pro-light-svg-icons";

import {
	faEnvelope,
	faGear,
	faPaperPlane,
	faRightToBracket,
} from "@fortawesome/pro-regular-svg-icons";

import { faLinkedin } from "@fortawesome/free-brands-svg-icons";

import { Icon, Chip, PermissionCheck } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { TableColumn } from "@/components/Table/Table";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { PermissionRoles, UserStatus } from "@/enum";
import { User } from "@/pages/Public/pages/Login/schema/login";
import { SortParams } from "@/types/types";
import { classNames } from "@/utils/classNames";

import { RECONNECT_LINKEDIN_DEEP_LINK } from "@/pages/Private/pages/Account/linkedinDeepLink";

import { userDisplayName, userInitials } from "@/utils/userName";

import { Email, isEmailLoggedOut } from "../../Account/schema/email";
import { getOrderStatus } from "../../List/utils/column-utils";
import { SendingUserCell } from "./SendingUsers";
import { UserLanguagesCell } from "./UserLanguagesCell";

/** Everything the column renderers need from the table that owns them. */
export interface UsersTableColumnDeps {
	t: TFunction;
	sortingUsers: SortParams;
	slotData?: Parameters<typeof SendingUserCell>[0]["slotData"];
	/** The signed-in user — only they get the inline "Connect LinkedIn" button. */
	currentUserId?: number;
	/** Set when an admin browses another company's users, which unlocks "log in as". */
	companyId?: string;
	/** Undefined while the feature flags load; the email-status column stays hidden until it is true. */
	isSequencerActive?: boolean;
	onToggleLinkedinAllowed: (user: User) => void;
	onOpenLinkedinSettings: (user: User) => void;
	onOpenEmails: (user: User) => void;
	onReinvite: (user: User) => void;
	onLoginAs: (user: User) => void;
	onEdit: (user: User) => void;
	onDelete: (user: User) => void;
	onNavigate: (path: string) => void;
}

const nameColumn = ({ t, sortingUsers }: UsersTableColumnDeps): TableColumn => ({
	title: t("basics.name"),
	minWidth: 170,
	uid: "name",
	orderTitle: "firstName",
	orderStatus: getOrderStatus(sortingUsers, "firstName"),
	render: (row: User) => (
		<div className="flex flex-row items-center">
			<span className="w-[26px] h-[26px] text-xs text-center text-white rounded-full bg-brand-light leading-[26px]">
				{userInitials(row)}
			</span>
			<div className="text-sm ml-1">{userDisplayName(row)}</div>
		</div>
	),
});

const emailColumn = ({ t, sortingUsers }: UsersTableColumnDeps): TableColumn => ({
	title: t("basics.email"),
	minWidth: 170,
	uid: "email",
	orderTitle: "email",
	orderStatus: getOrderStatus(sortingUsers, "email"),
	render: (row: User) => <div className="text-sm">{row.email}</div>,
});

const rolesColumn = ({ t }: UsersTableColumnDeps): TableColumn => ({
	title: t("basics.role"),
	minWidth: 100,
	uid: "roles",
	render: (row: User) => (
		<>
			{row.roles.map((role, index: number) => (
				<div key={index} className="text-sm">
					{role.name}
				</div>
			))}
		</>
	),
});

const linkedinAllowedColumn = (deps: UsersTableColumnDeps): TableColumn => ({
	title: deps.t("basics.linkedinAllowed"),
	uid: "linkedinAllowed",
	minWidth: 40,
	width: 40,
	render: (row: User) => (
		<SendingUserCell
			row={row}
			slotData={deps.slotData}
			onToggle={() => deps.onToggleLinkedinAllowed(row)}
		/>
	),
});

const linkedinStatusColumn = (deps: UsersTableColumnDeps): TableColumn => ({
	title: deps.t("basics.linkedinStatus"),
	uid: "isLoggedInInLinkedin",
	minWidth: 80,
	render: (row: User) => {
		if (!row.linkedinAllowed) {
			return <span className="text-sm text-gray-700">—</span>;
		}

		if (row.unipile?.isLoggedIn) {
			return (
				<div className="flex items-center gap-1.5">
					<div
						onClick={() => deps.onOpenLinkedinSettings(row)}
						className="inline-flex cursor-pointer flex-row w-fit items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium  bg-blue-action/10 border border-blue-action/10 text-blue-action whitespace-nowrap"
					>
						<span className="flex flex-row gap-1 items-center">
							<Icon className="w-3 h-3" icon={faLinkedin} />
							Connected
						</span>

						<Icon icon={faGear} />
					</div>
				</div>
			);
		}

		if (row.id !== deps.currentUserId) {
			return (
				<span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 border-gray-500/30 border text-gray-700 whitespace-nowrap">
					Not connected
				</span>
			);
		}

		return (
			<Button
				className="rounded-full!"
				color={ButtonColor.ACTION}
				size={ButtonSize.XS}
				title="Connect LinkedIn"
				onClick={() => deps.onNavigate(RECONNECT_LINKEDIN_DEEP_LINK)}
			/>
		);
	},
});

const emailStatusColumn = (deps: UsersTableColumnDeps): TableColumn => ({
	title: deps.t("basics.emailStatus"),
	uid: "emailStatus",
	minWidth: 140,
	render: (row: User) => {
		const connectedEmails =
			row.emails?.filter((email: Email) => !isEmailLoggedOut(email.status)).length ?? 0;
		const totalEmails = row.emails?.length ?? 0;
		const hasDisconnected = connectedEmails !== totalEmails;
		const hasEmails = totalEmails > 0;

		return (
			<button
				className={classNames(
					"items-center  cursor-pointer gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
					hasEmails && !hasDisconnected
						? "bg-green-200 border-green-500/30 text-green-700"
						: hasDisconnected
							? "bg-status-warning-light/30 border-yellow-200 text-status-warning"
							: "bg-gray-100 border-gray-500/30 text-gray-700",
					!row.linkedinAllowed && !hasEmails ? "hidden" : "inline-flex"
				)}
				disabled={!row.linkedinAllowed}
				onClick={() => deps.onOpenEmails(row)}
			>
				<Icon className="w-3 h-3 " icon={faEnvelope} />
				{hasEmails
					? hasDisconnected
						? `${connectedEmails}/${totalEmails} emails`
						: `${totalEmails} email${totalEmails !== 1 ? "s" : ""} connected`
					: "No emails connected"}
				<Icon className="w-2.5 h-2.5 s" icon={faGear} />
			</button>
		);
	},
});

/** PREC-3686: which languages this sender's AI drafts get written in. */
const languagesColumn = ({ t }: UsersTableColumnDeps): TableColumn => ({
	title: t("users.languages.label"),
	uid: "copywritingLanguages",
	minWidth: 120,
	render: (row: User) => <UserLanguagesCell user={row} />,
});

const statusColumn = ({ t }: UsersTableColumnDeps): TableColumn => ({
	title: t("basics.status"),
	uid: "status",
	minWidth: 100,
	render: (row: User) => {
		const ownerState =
			row.status === UserStatus.ACTIVE
				? "success"
				: row.status === UserStatus.BLOCKED
					? "error"
					: row.status === UserStatus.INVITED
						? "warning"
						: "secondary";

		return <Chip color={ownerState} label={row.status.toLowerCase()} />;
	},
});

const actionsColumn = (deps: UsersTableColumnDeps): TableColumn => ({
	title: deps.t("basics.actions"),
	uid: "actions",
	minWidth: 50,
	render: (row: User) => (
		<div className="flex flex-row justify-end gap-2">
			{(row.status === UserStatus.EXPIRED || row.status === UserStatus.INVITED) && (
				<div
					className="text-neutral-500 hover:cursor-pointer hover:text-primary-main"
					onClick={() => deps.onReinvite(row)}
				>
					<Tooltip content="Resend invite">
						<Icon alt="Resend invite" icon={faPaperPlane} size="lg" />
					</Tooltip>
				</div>
			)}
			{deps.companyId ? (
				<PermissionCheck requiredPermissions={[PermissionRoles.GLOBAL_ADMIN]}>
					<div
						className="text-neutral-500 hover:cursor-pointer hover:text-primary-main"
						onClick={() => deps.onLoginAs(row)}
					>
						<Icon icon={faRightToBracket} size="lg" />
					</div>
				</PermissionCheck>
			) : (
				<></>
			)}

			<div
				className="text-neutral-500 hover:cursor-pointer hover:text-primary-main"
				onClick={() => deps.onEdit(row)}
			>
				<Icon icon={faPenToSquare} size="lg" />
			</div>
			<div
				className="text-neutral-500 hover:cursor-pointer hover:text-primary-main"
				onClick={() => deps.onDelete(row)}
			>
				<Icon icon={faTrashCan} size="lg" />
			</div>
		</div>
	),
});

/**
 * The Users overview columns. Extracted from UsersTable so the table stays
 * under the file-length ratchet and each cell is testable on its own.
 */
export const buildUsersTableColumns = (deps: UsersTableColumnDeps): TableColumn[] =>
	[
		nameColumn(deps),
		emailColumn(deps),
		rolesColumn(deps),
		linkedinAllowedColumn(deps),
		linkedinStatusColumn(deps),
		emailStatusColumn(deps),
		languagesColumn(deps),
		statusColumn(deps),
		actionsColumn(deps),
	].filter((column) => deps.isSequencerActive || column.uid !== "emailStatus");
